import React, { useCallback, useEffect, useMemo, useState } from 'react';

import Box from '@material-ui/core/Box';
import CircularProgress from '@material-ui/core/CircularProgress';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import LinearProgress from '@material-ui/core/LinearProgress';
import Switch from '@material-ui/core/Switch';
import Typography from '@material-ui/core/Typography';

import {
  buildResendAdHocSendEventFailed,
  getAdHocCommunicationHistory
} from '../../../lib/services/communication.service';
import { useDebouncedValue } from '../../../lib/hooks/useDebouncedValue';
import { useCommunicationPanelContext } from '../CommunicationPanel/communication-panel-context';
import { SearchField } from '../../_core/_ui/search-field.component';
import { HistoryEventRow } from './history-event-row.component';
import { HistoryPager } from './history-pager.component';
import { isSendEventUnsettled } from './history-panel.helpers';
import { HISTORY_PAGE_SIZE, HISTORY_POLL_INTERVAL_MS } from './history-panel.constants';
import { SearchBar } from './history-panel.styles';

export const HistoryPanel: React.FC = () => {
  const { panelAssessmentId } = useCommunicationPanelContext();
  // One mutation observer for the whole list rather than one per row — each
  // row owns only its own retrying/retryError UI state and calls onResend.
  const { resendAdHocSendEventFailed } = buildResendAdHocSendEventFailed();
  const onResend = useCallback(
    (communicationGroupId: string) => resendAdHocSendEventFailed(communicationGroupId),
    [resendAdHocSendEventFailed]
  );

  const [query, setQuery] = useState('');
  const [page, setPage] = useState(0);
  const [includeTests, setIncludeTests] = useState(false);
  const debouncedQuery = useDebouncedValue(query.trim(), 300);

  // A new search or toggle resets to the first page so a narrowed result set
  // can't strand the user on an out-of-range page.
  useEffect(() => { setPage(0); }, [debouncedQuery, includeTests]);

  const { events, total, loading, error, startPolling, stopPolling } = getAdHocCommunicationHistory({
    assessmentId: panelAssessmentId,
    search: debouncedQuery,
    includeTests,
    offset: page * HISTORY_PAGE_SIZE,
    limit: HISTORY_PAGE_SIZE
  });

  // Poll only while a visible event is unsettled — the refetch reconciles this
  // fire-and-track send; cleanup stops it the moment everything settles or unmounts.
  const anyUnsettled = useMemo(() => events.some(isSendEventUnsettled), [events]);
  useEffect(() => {
    if (!anyUnsettled) return undefined;
    startPolling(HISTORY_POLL_INTERVAL_MS);
    return () => stopPolling();
  }, [anyUnsettled, startPolling, stopPolling]);

  if (!panelAssessmentId) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="textSecondary">
          No assessment available to view send history.
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Typography variant="body2" color="error">
          Couldn’t load history. Try again in a moment.
        </Typography>
      </Box>
    );
  }

  let content;
  if (loading && events.length === 0) {
    content = (
      <Box p={3} display="flex" justifyContent="center">
        <CircularProgress size={20} />
      </Box>
    );
  } else if (total === 0) {
    content = (
      <Box p={3}>
        <Typography variant="body2" color="textSecondary">
          {debouncedQuery ? 'No sends match your search.' : 'No messages sent yet.'}
        </Typography>
      </Box>
    );
  } else {
    content = (
      <>
        {events.map((event) => (
          <HistoryEventRow
            key={event._id}
            event={event}
            assessmentId={panelAssessmentId}
            onResend={onResend}
          />
        ))}
        <HistoryPager page={page} pageSize={HISTORY_PAGE_SIZE} total={total} onPageChange={setPage} />
      </>
    );
  }

  return (
    <Box>
      <SearchBar>
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Search sends or recipients"
          ariaLabel="Search send history"
        />
        <Box mt={0.5}>
          <FormControlLabel
            control={(
              <Switch
                size="small"
                color="primary"
                checked={includeTests}
                onChange={(e) => setIncludeTests(e.target.checked)}
              />
            )}
            label={<Typography variant="caption" color="textSecondary">Show test sends</Typography>}
          />
        </Box>
      </SearchBar>
      {/* Thin in-flight bar while a page/search refetch resolves over existing
          rows; the centered spinner in `content` covers the empty-list case. */}
      {loading && events.length > 0 && <LinearProgress />}
      {content}
    </Box>
  );
};
